import { HUBSPOT_PROXY_URL } from "../config/api";
import { turnoverWrite as sqlWrite, sqlRead, sqlEscape } from "./sqlClient";
import { authHeader } from "./apiToken";
import { ACTIVE_PIPELINE_STATUSES } from "../config/dashboardStatuses";

// ── HubSpot proxy helper ──────────────────────────────────────────────────────
// All HubSpot API calls go through the cs-est-hubspot-api function app — HubSpot's CORS
// policy blocks direct browser calls.
export async function hubspotProxy(url, method = "GET", body = null) {
  const res = await fetch(HUBSPOT_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: JSON.stringify({ url, method, body }),
  });
  if (!res.ok) throw new Error(`HubSpot proxy failed (${res.status})`);
  return res.json();
}

const HS_COMPANY_PROPS   = "name,address,city,state,zip,phone,website";
const HS_LINE_ITEM_PROPS = "name,amount,quantity,price,awarded,release_date,end_date,description,squarefootage,dollar_per_sf";
const HS_CONTACT_PROPS   = "firstname,lastname,jobtitle,email,phone";

const delay = ms => new Promise(r => setTimeout(r, ms));

// ── Core HubSpot sync ─────────────────────────────────────────────────────────
// Fetches a deal's associated companies/contacts/line items + description
// from HubSpot, and — when turnoverId is provided — writes the result to
// TurnoverHubSpotCache via sp_SaveTurnoverHubSpotCache so it's available on
// the very next sp_GetTurnoverDetail read without another round trip.
//
// Used from two places:
//   - Turnovers.jsx (NewTurnoverModal.handleCreate), right after
//     sp_CreateTurnover returns the new TurnoverID, so a freshly created
//     turnover already has this data cached the first time anyone opens it.
//   - TurnoverDetail.jsx (loadHubSpotCompanies), on first Cover Sheet open
//     and on manual "Refresh HubSpot" clicks.
//
// Sequential fetches (with short delays) avoid overwhelming the HubSpot
// proxy function app — mirrors the original inline implementation.
//
// Returns null if no dealId. Throws only on the HubSpot fetch itself failing
// (network/API errors the caller should surface). A cache-write failure does
// NOT throw — it's returned as `cacheError` so the caller can still display
// the freshly-fetched data and decide how to notify the person, rather than
// losing a good fetch to a bad write.
export async function syncHubSpotData(dealId, turnoverId, currentUser) {
  if (!dealId) return null;

  // Deal description and square footage — kept current on every sync since
  // these can change in HubSpot after the turnover is created.
  // Internal HubSpot property name for square footage: "square_feet"
  let dealDescription = null;
  let squareFeet = null;
  try {
    const dealProps = await hubspotProxy(`https://api.hubapi.com/crm/v3/objects/deals/${dealId}?properties=description,square_feet`);
    dealDescription = dealProps?.properties?.description ?? null;
    squareFeet      = dealProps?.properties?.square_feet  ?? null;
    await delay(500);
  } catch {}

  const companyAssoc  = await hubspotProxy(`https://api.hubapi.com/crm/v4/objects/deals/${dealId}/associations/companies`);
  await delay(500);
  const contactAssoc  = await hubspotProxy(`https://api.hubapi.com/crm/v4/objects/deals/${dealId}/associations/contacts`);
  await delay(500);
  const lineItemAssoc = await hubspotProxy(`https://api.hubapi.com/crm/v4/objects/deals/${dealId}/associations/line_items`);
  await delay(500);

  const rawContacts  = contactAssoc.results  || [];
  const rawCompanies = companyAssoc.results  || [];
  const rawLineItems = lineItemAssoc.results || [];

  // Batch read all objects in parallel (3 calls max regardless of count)
  const batchRead = async (objectType, ids, properties) => {
    if (!ids.length) return [];
    const body = {
      properties,
      inputs: ids.map(id => ({ id: String(id) })),
    };
    const res = await hubspotProxy(
      `https://api.hubapi.com/crm/v3/objects/${objectType}/batch/read`,
      "POST",
      body
    );
    return res.results || [];
  };

  const contactDetails  = await batchRead("contacts",   rawContacts.map(c => c.toObjectId || c.id),  HS_CONTACT_PROPS.split(","));
  await delay(500);
  const companyDetails  = await batchRead("companies",  rawCompanies.map(c => c.toObjectId || c.id), HS_COMPANY_PROPS.split(","));
  await delay(500);
  const lineItemDetails = await batchRead("line_items", rawLineItems.map(c => c.toObjectId || c.id), HS_LINE_ITEM_PROPS.split(","));

  // Build lookup maps by ID
  const contactMap  = Object.fromEntries(contactDetails.map(c => [String(c.id), c.properties || {}]));
  const companyMap  = Object.fromEntries(companyDetails.map(c => [String(c.id), c.properties || {}]));
  const lineItemMap = Object.fromEntries(lineItemDetails.map(c => [String(c.id), c.properties || {}]));

  // Map contacts with labels — one row per label per contact
  const contacts = rawContacts.flatMap(c => {
    const id = String(c.toObjectId || c.id);
    const p = contactMap[id] || {};
    const name  = [p.firstname, p.lastname].filter(Boolean).join(" ") || "";
    const title = p.jobtitle || "";
    const email = p.email    || "";
    const phone = p.phone    || "";
    const labels = c.associationTypes
      ?.filter(t => t.category === "USER_DEFINED")
      .map(t => t.label)
      .filter(Boolean) || [];
    if (labels.length === 0) {
      const fallback = c.associationTypes?.[0]?.label || "Contact";
      return [{ id, label: fallback, name, title, email, phone }];
    }
    return labels.map(label => ({ id, label, name, title, email, phone }));
  });

  // Map companies with labels — one row per label per company
  const companies = rawCompanies.flatMap(c => {
    const id = String(c.toObjectId || c.id);
    const details = companyMap[id] || {};
    const labels = c.associationTypes
      ?.filter(t => t.category === "USER_DEFINED")
      .map(t => t.label)
      .filter(Boolean) || [];
    if (labels.length === 0) {
      const fallback = c.associationTypes?.[0]?.label || "Company";
      return [{ id, label: fallback, ...details }];
    }
    return labels.map(label => ({ id, label, ...details }));
  });

  // Map line items
  const lineItems = rawLineItems.map(c => {
    const id = String(c.toObjectId || c.id);
    return { id, ...lineItemMap[id] };
  });

  const result = { companies, contacts, lineItems, dealDescription, squareFeet, refreshedAt: null, cacheError: null };

  if (turnoverId) {
    try {
      const cachePayload = sqlEscape(JSON.stringify({
        turnover_id:      parseInt(turnoverId),
        companies_json:   companies,
        contacts_json:    contacts,
        line_items_json:  lineItems,
        refreshed_by:     currentUser?.name || "",
        deal_description: dealDescription,
        square_feet:      squareFeet,
      }));
      await sqlWrite(`EXEC sp_SaveTurnoverHubSpotCache @payload = '${cachePayload}'`);
      result.refreshedAt = new Date().toISOString();
    } catch (cacheErr) {
      result.cacheError = cacheErr.message;
    }
  }

  return result;
}

// ── Dashboard status sync ─────────────────────────────────────────────────────
// Batch-reads current dealstage + amount for every deal shown on the dashboard,
// resolves stage labels via the pipelines API, then writes the results to
// sp_SyncDashboardStatuses which updates Status/HubSpotAmount and deactivates
// any deal whose stage is no longer in the active set.
//
// Returns { deactivated: Set<ProjectID>, updated: Map<ProjectID, changes> }
// so the caller can filter/update its in-memory rows without a full reload.
export async function syncDashboardStatuses(rows) {
  const hsRows = rows.filter(r => r.HubSpotDealID);
  if (!hsRows.length) return { deactivated: new Set(), updated: new Map() };

  // Fetch all pipeline stage definitions once to build stageId → label.
  // One retry absorbs a transient HubSpot blip; if it still fails, stageMap
  // stays empty and every deal's status below resolves to null — the sync
  // then leaves Status/IsActive untouched rather than writing a raw HubSpot
  // internal stage id, which used to silently deactivate real, active
  // estimates (that raw id never matches sp_SyncDashboardStatuses's
  // allowlist; found 2026-09-21).
  const stageMap = {};
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const pipelines = await hubspotProxy("https://api.hubapi.com/crm/v3/pipelines/deals");
      for (const pipeline of (pipelines?.results || [])) {
        for (const stage of (pipeline.stages || [])) {
          stageMap[stage.id] = stage.label;
        }
      }
      break;
    } catch (e) {
      if (attempt === 0) { await delay(500); continue; }
      console.warn("syncDashboardStatuses: could not fetch pipelines after retry — statuses left unchanged this round", e);
    }
  }

  // Batch-read dealstage + amount in chunks of 100 (HubSpot per-request limit)
  const CHUNK = 100;
  const ids = hsRows.map(r => r.HubSpotDealID);
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));

  const dealResults = (await Promise.all(
    chunks.map(chunk =>
      hubspotProxy(
        "https://api.hubapi.com/crm/v3/objects/deals/batch/read",
        "POST",
        { properties: ["dealstage", "amount"], inputs: chunk.map(id => ({ id: String(id) })) }
      ).then(r => r.results || []).catch(() => [])
    )
  )).flat();

  if (!dealResults.length) return { deactivated: new Set(), updated: new Map() };

  // Build the sync payload
  const syncPayload = dealResults.map(deal => ({
    hubspot_deal_id: String(deal.id),
    // No raw-dealstage-id fallback here on purpose — see the stageMap
    // comment above. An unresolved status is sent as null.
    status: stageMap[deal.properties?.dealstage] ?? null,
    hubspot_amount: deal.properties?.amount != null ? parseFloat(deal.properties.amount) : null,
  }));

  // Write to DB — returns updated rows with new IsActive/Status/HubSpotAmount
  const dbRows = await sqlRead(
    `EXEC sp_SyncDashboardStatuses @payload = '${sqlEscape(JSON.stringify(syncPayload))}'`
  );

  // Build diff maps so the caller can patch its state rather than reload
  const deactivated = new Set();
  const updated = new Map();

  for (const db of (dbRows || [])) {
    const projectId = db.ProjectID;
    if (!db.IsActive) {
      deactivated.add(projectId);
      continue;
    }
    const orig = rows.find(r => r.ProjectID === projectId);
    const changes = {};
    if (orig && db.Status !== orig.Status) changes.Status = db.Status;
    if (db.HubSpotAmount != null && db.HubSpotAmount !== orig?.HubSpotAmount)
      changes.HubSpotAmount = db.HubSpotAmount;
    if (Object.keys(changes).length) updated.set(projectId, changes);
  }

  return { deactivated, updated };
}

// ── Estimate record sync ──────────────────────────────────────────────────────
// Fetches deal-level properties from HubSpot (owner, estimator, stage label,
// bid due date), resolves names via the Owners API, and writes them back to
// the Estimates row via sp_SaveEstimate. Called from EstCoverSheet on load so
// the list stays current without requiring a webhook trigger.
export async function syncEstimateRecord(dealId, projectId) {
  if (!dealId || !projectId) return null;
  try {
    const deal = await hubspotProxy(
      `https://api.hubapi.com/crm/v3/objects/deals/${dealId}?properties=dealname,hubspot_owner_id,estimators,dealstage,pipeline,proposal_due_date,delivery_method,hs_folder_link,room_type`
    );
    const props = deal?.properties || {};

    const resolveOwner = async (ownerId) => {
      if (!ownerId) return { name: null, email: null };
      try {
        const owner = await hubspotProxy(`https://api.hubapi.com/crm/v3/owners/${ownerId}`);
        return {
          name:  [owner.firstName, owner.lastName].filter(Boolean).join(" ") || owner.email || null,
          email: owner.email || null,
        };
      } catch { return { name: null, email: null }; }
    };

    // See syncDashboardStatuses's stageMap comment above — never fall back to
    // the raw HubSpot stage id here; it silently deactivates real estimates
    // once written to Status.
    const resolveStageName = async (stageId) => {
      if (!stageId) return null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const data = await hubspotProxy(`https://api.hubapi.com/crm/v3/pipelines/deals`);
          for (const pipeline of (data?.results || [])) {
            const stage = pipeline.stages?.find(s => s.id === stageId);
            if (stage) return stage.label;
          }
          return null;
        } catch {
          if (attempt === 0) { await delay(500); continue; }
          return null;
        }
      }
      return null;
    };

    const [dealOwner, estimator, stageName] = await Promise.all([
      resolveOwner(props.hubspot_owner_id),
      resolveOwner(props.estimators),
      resolveStageName(props.dealstage),
    ]);

    const payload = sqlEscape(JSON.stringify({
      hubspot_deal_id:       String(dealId),
      project_name:          props.dealname           || null,
      deal_owner:            dealOwner.name           || null,
      estimator:             estimator.name           || null,
      status:                stageName                || null,
      bid_due_date:          props.proposal_due_date  || null,
      delivery_method:       props.delivery_method    || null,
      sharepoint_folder_url: props.hs_folder_link     || null,
      project_type:          props.room_type          || null,
    }));
    await sqlRead(`EXEC sp_SaveEstimate @payload = '${payload}'`);

    return {
      dealOwnerName:  dealOwner.name,
      dealOwnerEmail: dealOwner.email,
      estimatorName:  estimator.name,
      stageName,
      projectType:    props.room_type || null,
    };
  } catch (e) {
    console.error("syncEstimateRecord failed:", e);
    return null;
  }
}

// ── Reactivation preview ──────────────────────────────────────────────────────
// Explicitly triggered (not run on every dashboard load — this is a batch of
// HubSpot calls over every currently-inactive linked estimate, which can be
// dozens). Answers "if the sync ran right now, which of these deactivated
// estimates would come back?" — the same check used manually to review the
// 2026-09-21 mass-reactivation before deploying sp_SyncDashboardStatuses's
// bidirectional fix, now available as a self-serve dashboard action so an
// estimator can lock (src/utils/hubspotSync.js's sibling
// sp_SetEstimateSyncLock call, see setEstimateSyncLock below) anything
// HubSpot still shows as active/in-play that estimating knows is actually
// stale, before the nightly dashboard-reconcile job reactivates it.
//
// deals: [{ ProjectID, HubSpotDealID, Status }] — already-inactive estimates
// with a HubSpotDealID (e.g. sp_GetHubSpotLinkedEstimates rows filtered to
// !IsActive). Returns each with dealName/liveLabel/wouldReactivate added;
// wouldReactivate is false (not guessed true) when the live stage couldn't
// be resolved, same fail-safe convention as the sync procs.
export async function previewReactivations(deals) {
  const withDealId = deals.filter(d => d.HubSpotDealID);
  if (!withDealId.length) return [];

  const stageMap = {};
  try {
    const pipelines = await hubspotProxy("https://api.hubapi.com/crm/v3/pipelines/deals");
    for (const pipeline of (pipelines?.results || [])) {
      for (const stage of (pipeline.stages || [])) stageMap[stage.id] = stage.label;
    }
  } catch (e) {
    console.warn("previewReactivations: could not fetch pipelines", e);
    return withDealId.map(d => ({ ...d, dealName: null, liveLabel: null, wouldReactivate: false }));
  }

  const CHUNK = 100;
  const dealResults = [];
  for (let i = 0; i < withDealId.length; i += CHUNK) {
    const chunk = withDealId.slice(i, i + CHUNK);
    try {
      const res = await hubspotProxy("https://api.hubapi.com/crm/v3/objects/deals/batch/read", "POST", {
        properties: ["dealstage", "dealname"],
        inputs: chunk.map(d => ({ id: String(d.HubSpotDealID) })),
      });
      dealResults.push(...(res.results || []));
    } catch (e) {
      console.warn(`previewReactivations: batch/read failed for deals ${i}-${i + chunk.length}`, e);
    }
    if (i + CHUNK < withDealId.length) await delay(200);
  }
  const liveById = Object.fromEntries(dealResults.map(d => [String(d.id), d.properties || {}]));

  return withDealId.map(d => {
    const live = liveById[d.HubSpotDealID];
    const liveLabel = live ? (stageMap[live.dealstage] ?? null) : null;
    return {
      ...d,
      dealName: live?.dealname || null,
      liveLabel,
      wouldReactivate: !!liveLabel && ACTIVE_PIPELINE_STATUSES.includes(liveLabel),
    };
  });
}

// ── Sync lock (estimator override) ────────────────────────────────────────────
// Freezes an estimate's Status/IsActive against every HubSpot sync path —
// sp_SyncDashboardStatuses and sp_SaveEstimate (db/02_projects.sql) both skip
// a locked row entirely. For when HubSpot itself is stale for a specific
// deal and Sales hasn't corrected it yet; never writes back to HubSpot.
// payload: { project_id, locked, reason (required to lock), locked_by,
//   also_set_active (optional 0/1) }.
export async function setEstimateSyncLock(payload) {
  const json = sqlEscape(JSON.stringify(payload));
  const rows = await sqlRead(`EXEC sp_SetEstimateSyncLock @payload = '${json}'`);
  return rows?.[0] || null;
}
